import { Banner, Button, Field, Select } from '@cloudflare/kumo';
import { useEffect, useMemo, useState } from 'react';
import { funnelApi, type Deal, type Pipeline } from '../services/funnelApi';
import { KanbanBoard } from '../components/Funil/KanbanBoard';
import { FunnelMetrics } from '../components/Funil/FunnelMetrics';
import { DealDetail } from '../components/Funil/DealDetail';
import { DealForm } from '../components/Funil/DealForm';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchField } from '../components/ui/SearchField';
import { ICONS } from '../components/ui/icons';
import '../components/Funil/funil.css';
import { SkeletonKanbanCards } from '../components/ui/Skeleton';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Em aberto' },
  { value: 'won', label: 'Ganhos' },
  { value: 'lost', label: 'Perdidos' },
  { value: 'all', label: 'Todos' },
];

/**
 * Funil de vendas — página principal (P2 #159).
 *
 * Carrega pipelines + deals em paralelo. Filtros simples (pipeline / status /
 * busca) — busca é debounce no cliente para evitar uma chamada por tecla.
 * Métricas calculadas localmente a partir dos deals retornados.
 */
export function FunilPage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [defaultPipelineId, setDefaultPipelineId] = useState<string | null>(null);
  const [pipelineId, setPipelineId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'open' | 'won' | 'lost' | 'all'>('open');
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Carregar pipelines uma vez
  useEffect(() => {
    let mounted = true;
    funnelApi
      .pipelines()
      .then((res) => {
        if (!mounted) return;
        setPipelines(res.pipelines ?? []);
        const def = res.defaultPipelineId ?? res.pipelines?.[0]?.id ?? null;
        setDefaultPipelineId(def);
        setPipelineId(def ?? '');
      })
      .catch((e) => mounted && setErr(e?.message ?? 'Falha ao carregar pipelines'));
    return () => { mounted = false; };
  }, []);

  // Debounce da busca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Carregar deals quando muda pipeline / status / busca
  useEffect(() => {
    if (!pipelineId) return;
    let mounted = true;
    setLoading(true);
    setErr(null);
    funnelApi
      .deals({
        pipelineId,
        status: statusFilter === 'all' ? null : statusFilter.toUpperCase(),
        search: debouncedSearch || undefined,
        pageSize: 100,
      })
      .then((res) => mounted && setDeals(res.deals ?? []))
      .catch((e) => mounted && setErr(e?.message ?? 'Falha ao carregar negócios'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [pipelineId, statusFilter, debouncedSearch]);

  const selectedDeal = useMemo(
    () => deals.find((d) => d.id === selectedDealId) ?? null,
    [deals, selectedDealId],
  );

  const metrics = useMemo(() => {
    const open = deals.filter((d) => d.status === 'OPEN');
    const won = deals.filter((d) => d.status === 'WON');
    const lost = deals.filter((d) => d.status === 'LOST');
    const openValue = open.reduce((acc, d) => acc + (Number(d.amount) || 0), 0);
    const overallConversion = won.length + lost.length > 0 ? won.length / (won.length + lost.length) : 0;
    const byStage = (pipelines.find((p) => p.id === pipelineId)?.stages ?? []).map((s) => {
      const stageDeals = deals.filter((d) => d.stageId === s.id);
      const stageWon = stageDeals.filter((d) => d.status === 'WON').length;
      const conv = stageDeals.length > 0 ? stageWon / stageDeals.length : 0;
      return { stageId: s.id, stageName: s.name, conversion: conv, deals: stageDeals.length };
    });
    return {
      openCount: open.length,
      openValue,
      wonCount: won.length,
      lostCount: lost.length,
      overallConversion,
      conversionByStage: byStage,
    };
  }, [deals, pipelines, pipelineId]);

  const handleDealCreated = (deal: Deal) => {
    setDeals((prev) => [deal, ...prev]);
    setShowForm(false);
  };

  const pipelineOptions = pipelines.map((p) => ({ value: p.id, label: p.name }));
  const pipelineOption = pipelineOptions.find((o) => o.value === pipelineId) ?? null;
  const statusOption = STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? null;

  return (
    <div className="funil-page" data-testid="funil-page">
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4">
          <div style={{ maxWidth: 760 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Funil de vendas</h2>
            <div className="text-kumo-subtle">Negócios por estágio. Arraste os cartões entre as colunas ou use o menu “…” para mover.</div>
          </div>
          <Button variant="primary" icon={ICONS.Plus} onClick={() => setShowForm(true)} data-testid="funil-new-deal">
            Novo negócio
          </Button>
        </div>
      </div>

      <div className="page-content funil-body">
      <div className="funil-filters" role="region" aria-label="Filtros do funil">
        <Field label="Pipeline">
          <Select value={(pipelineOption)?.value ?? null} items={pipelineOptions} onValueChange={(value: string | null) => {
              const v = value;
              if (v) setPipelineId(v);
            }} disabled={pipelines.length === 0} data-testid="funil-filter-pipeline" />
        </Field>
        <Field label="Status">
          <Select value={(statusOption)?.value ?? null} items={STATUS_OPTIONS} onValueChange={(value: string | null) => {
              const v = value;
              if (v) setStatusFilter(v as 'open' | 'won' | 'lost' | 'all');
            }} data-testid="funil-filter-status" />
        </Field>
        <SearchField
          ariaLabel="Buscar negócio pelo título"
          placeholder="Buscar pelo título do negócio"
          value={search}
          onChange={setSearch}
          data-testid="funil-filter-search"
        />
      </div>

      <FunnelMetrics pipelines={pipelines} metrics={metrics} />

      {err ? <Banner variant="error" data-testid="funil-error" description={<>{err}</>} /> : null}

      {loading && deals.length === 0 ? (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>{Array.from({ length: 4 }, (_, i) => <SkeletonKanbanCards key={i} count={3 - (i % 2)} />)}</div>
      ) : !loading && deals.length === 0 ? (
        <EmptyState
          title="Nenhum negócio"
          description="Crie seu primeiro negócio para começar a usar o funil."
          data-testid="funil-empty-state"
        />
      ) : (
        <KanbanBoard
          pipelines={pipelines}
          pipelineId={pipelineId}
          deals={deals}
          selectedDealId={selectedDealId}
          onSelectDeal={setSelectedDealId}
          onDealsChanged={setDeals}
        />
      )}

      {selectedDeal ? (
        <DealDetail
          deal={selectedDeal}
          onClose={() => setSelectedDealId(null)}
        />
      ) : null}

      {showForm ? (
        <DealForm
          pipelines={pipelines}
          defaultPipelineId={defaultPipelineId}
          onCreated={handleDealCreated}
          onClose={() => setShowForm(false)}
        />
      ) : null}
      </div>
    </div>
  );
}

export default FunilPage;
